import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStore } from '../context/StoreContext';
import { useAuth } from '../context/AuthContext';
import { storesAPI } from '../api';

const API_BASE = process.env.REACT_APP_API_URL?.replace('/api/v1', '') || 'http://localhost:8001';

const STORE_TYPES = [
  { value: 'small_retail', label: 'Small Retail Shop' },
  { value: 'supermarket', label: 'Supermarket / Hypermarket' },
  { value: 'jewellery', label: 'Jewellery Shop' },
  { value: 'bridal_rental', label: 'Bridal Rental Shop' },
];

const STORE_TYPE_DESCRIPTIONS = {
  bridal_rental: 'Manage bookings, rentals, and returns',
  small_retail: 'Manage product sales and inventory',
  supermarket: 'Fast billing and stock tracking',
};

const CURRENCIES = [
  { value: 'USD', label: 'USD \u2014 US Dollar' },
  { value: 'EUR', label: 'EUR \u2014 Euro' },
  { value: 'GBP', label: 'GBP \u2014 British Pound' },
  { value: 'SAR', label: 'SAR \u2014 Saudi Riyal' },
  { value: 'AED', label: 'AED \u2014 UAE Dirham' },
  { value: 'INR', label: 'INR \u2014 Indian Rupee' },
  { value: 'KWD', label: 'KWD \u2014 Kuwaiti Dinar' },
  { value: 'BHD', label: 'BHD \u2014 Bahraini Dinar' },
  { value: 'QAR', label: 'QAR \u2014 Qatari Riyal' },
  { value: 'OMR', label: 'OMR \u2014 Omani Rial' },
  { value: 'EGP', label: 'EGP \u2014 Egyptian Pound' },
  { value: 'JOD', label: 'JOD \u2014 Jordanian Dinar' },
  { value: 'MAD', label: 'MAD \u2014 Moroccan Dirham' },
  { value: 'TRY', label: 'TRY \u2014 Turkish Lira' },
  { value: 'PKR', label: 'PKR \u2014 Pakistani Rupee' },
];

// ----------------

const SelectStore = () => {
  const { stores, loading, error, selectStore, createStore, loadStores } = useStore();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    store_type: 'bridal_rental',
    currency: '',
    address: '',
    phone: '',
    email: '',
    brand_name: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [formError, setFormError] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('create') === '1') {
      setShowCreateForm(true);
    }
  }, [location.search]);

  // Always fetch stores when this page mounts — safe because PrivateRoute
  // for /select-store does NOT use requireStore, so storeLoading won't
  // cause an unmount/remount loop.
  useEffect(() => {
    loadStores();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select when user has exactly one store — skip the selection screen entirely.
  useEffect(() => {
    if (!loading && stores?.length === 1 && !showCreateForm) {
      selectStore(stores[0]);
      navigate('/dashboard', { replace: true });
    }
  }, [loading, stores, showCreateForm, selectStore, navigate]);

  const handleSelectStore = (store) => {
    selectStore(store);
    navigate('/dashboard');
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) {
      setFormError('Invalid file type. Allowed: PNG, JPEG, WebP, SVG');
      return;
    }

    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
    setFormError('');
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCreateStore = async (e) => {
    e.preventDefault();
    setCreating(true);
    setFormError('');

    const result = await createStore(formData);

    if (result.success) {
      if (logoFile && result.store?.id) {
        try {
          const uploadRes = await storesAPI.uploadLogo(result.store.id, logoFile);
          selectStore(uploadRes.data);
        } catch (err) {
          selectStore(result.store);
        }
      } else {
        selectStore(result.store);
      }
      setCreating(false);
      navigate('/dashboard');
    } else {
      setCreating(false);
      setFormError(typeof result.error === 'string' ? result.error : 'Failed to create store');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getLogoSrc = (store) => {
    if (!store.logo_url) return null;
    if (store.logo_url.startsWith('http')) return store.logo_url;
    return `${API_BASE}${store.logo_url}`;
  };

  const getInitials = (store) => {
    return (store.brand_name || store.name)
      .split(' ')
      .map(w => w[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="loading store-select-loading">
        <div className="spinner"></div>
        <span>Loading stores...</span>
      </div>
    );
  }

  return (
    <div className="store-select-container">
      <div className="store-select-bg">
        <span className="store-select-bg-orb store-select-bg-orb--1"></span>
        <span className="store-select-bg-orb store-select-bg-orb--2"></span>
        <span className="store-select-bg-orb store-select-bg-orb--3"></span>
      </div>
      <div className="store-select-inner">
        <div className="store-select-header">
          <div className="store-select-logo-wrapper">
            <span className="store-select-logo-aura"></span>
            <div className="store-select-logo-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path>
              </svg>
            </div>
          </div>
          <div className="store-select-brand">Aiviora</div>
          <h1 className="store-select-title">Select Your Store</h1>
          <p>Choose a store to manage or create a new one</p>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        {stores.length > 0 ? (
          <div className="store-grid">
            {stores.map((store) => (
              <div
                key={store.id}
                className="store-card"
                onClick={() => handleSelectStore(store)}
              >
                <div className="store-card-arrow">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </div>
                <div className="store-card-header">
                  {store.logo_url ? (
                    <img
                      src={getLogoSrc(store)}
                      alt={store.brand_name || store.name}
                      className="store-card-logo"
                    />
                  ) : (
                    <div className="store-card-initials">
                      {getInitials(store)}
                    </div>
                  )}
                  <div>
                    <div className="store-card-name">{store.brand_name || store.name}</div>
                    <div className="store-card-type">{store.store_type.replace('_', ' ')}</div>
                  </div>
                </div>
                <div className="store-card-meta">
                  {store.address && (
                    <div className="store-card-address">{store.address}</div>
                  )}
                  <div className="store-card-modules">
                    <span className="badge badge-primary">
                      {store.modules?.filter(m => m.enabled).length || 0} modules
                    </span>
                  </div>
                </div>
              </div>
            ))}

            <button
              className="store-card-create"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              <div className="store-card-create-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </div>
              <div>
                <div className="store-card-create-text">Create New Store</div>
                <div className="store-card-create-sub">Set up a new workspace</div>
              </div>
            </button>
          </div>
        ) : (
          <div className="store-select-empty">
            <div className="store-select-empty-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                <polyline points="9 22 9 12 15 12 15 22"></polyline>
              </svg>
            </div>
            <h3>No stores yet</h3>
            <p>Create your first store to get started</p>
          </div>
        )}

        {stores.length === 0 && (
          <div className="store-select-actions">
            <button
              className="btn btn-primary"
              onClick={() => setShowCreateForm(!showCreateForm)}
            >
              {showCreateForm ? 'Cancel' : 'Create New Store'}
            </button>
          </div>
        )}

        {showCreateForm && (
          <div className="card store-create-card">
            <div className="card-header">
              <h3 className="card-title">Create New Store</h3>
            </div>
            <div className="card-body">
              {formError && <div className="alert alert-danger">{formError}</div>}

              <form onSubmit={handleCreateStore}>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Store Name *</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      placeholder="My Store"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Store Type *</label>
                    <select
                      className="form-select"
                      value={formData.store_type}
                      onChange={(e) => setFormData({ ...formData, store_type: e.target.value })}
                      required
                    >
                      {STORE_TYPES.map((type) => (
                        <option key={type.value} value={type.value}>
                          {type.label}
                        </option>
                      ))}
                    </select>
                    <span className="form-help-text">
                      {STORE_TYPE_DESCRIPTIONS[formData.store_type] || 'Choose a store type to continue'}
                    </span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Currency *</label>
                    <select
                      className="form-select"
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      required
                    >
                      <option value="" disabled>Select currency</option>
                      {CURRENCIES.map((c) => (
                        <option key={c.value} value={c.value}>
                          {c.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Brand / Display Name</label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.brand_name}
                      onChange={(e) => setFormData({ ...formData, brand_name: e.target.value })}
                      placeholder="My Brand"
                    />
                    <span className="form-help-text">Optional. Shown in the sidebar. Defaults to store name.</span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Logo</label>
                    <div className="logo-upload-area">
                      {logoPreview ? (
                        <div className="logo-preview">
                          <img src={logoPreview} alt="Logo preview" />
                          <button type="button" className="logo-remove-btn" onClick={handleRemoveLogo}>
                            &times;
                          </button>
                        </div>
                      ) : (
                        <label className="logo-upload-trigger" htmlFor="logo-file">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <circle cx="8.5" cy="8.5" r="1.5"></circle>
                            <polyline points="21 15 16 10 5 21"></polyline>
                          </svg>
                          <span>Upload logo</span>
                        </label>
                      )}
                      <input
                        id="logo-file"
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        onChange={handleLogoChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                    <span className="form-help-text">Optional. PNG, JPEG, WebP or SVG.</span>
                  </div>

                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+1 (555) 000-0000"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="store@example.com"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Address</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="123 Main Street, City"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={creating}
                  >
                    {creating ? 'Creating...' : 'Create Store'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="store-select-footer">
          <button className="store-select-signout" onClick={handleLogout}>
            Sign out
          </button>
        </div>
      </div>
    </div>
  );
};

export default SelectStore;
